import { createSlice } from "@reduxjs/toolkit";

const cartSlice = createSlice({
  name: "cart",
  initialState: {
    resDetails: {
      id: null,
      resName: "",
      resArea: "",
      resImg: "",
      resSla: {},
    },
    cartItems: [],
    isLoading: false,
    error: null,
    cartMeta: {
      totalItems: 0,
      totalAmount: 0,
    },
  },
  reducers: {
    // Set loading state
    setCartLoading: (state, action) => {
      state.isLoading = action.payload;
    },
    
    // Set cart error
    setCartError: (state, action) => {
      state.error = action.payload;
    },
    
    // Clear cart error
    clearCartError: (state) => {
      state.error = null;
    },
    
    // Add item to cart or increment quantity if exists
    addItems: (state, action) => {
      const existingItem = state.cartItems.find(
        (item) => item.id === action.payload.id
      );
      
      if (existingItem) {
        // Item exists, increment quantity
        existingItem.quantity += 1;
        if (existingItem.unit_price) {
          existingItem.total_price = existingItem.unit_price * existingItem.quantity;
        }
      } else {
        // New item, add to cart with quantity 1
        state.cartItems.push({ 
          ...action.payload, 
          quantity: 1,
          total_price: action.payload.unit_price || action.payload.price || 0
        });
      }
      
      // Update meta
      state.cartMeta.totalItems = state.cartItems.reduce((sum, item) => sum + item.quantity, 0);
      state.cartMeta.totalAmount = state.cartItems.reduce((sum, item) => sum + item.total_price, 0);
    },
    
    // Remove item from cart or decrement quantity
    removeItems: (state, action) => {
      const existingItemIndex = state.cartItems.findIndex(
        (item) => item.id === action.payload.id
      );
      
      if (existingItemIndex === -1) return;
      
      const existingItem = state.cartItems[existingItemIndex];
      
      if (existingItem.quantity > 1) {
        // Decrement quantity
        existingItem.quantity -= 1;
        if (existingItem.unit_price) {
          existingItem.total_price = existingItem.unit_price * existingItem.quantity;
        }
      } else {
        // Remove item completely
        state.cartItems.splice(existingItemIndex, 1);
      }
      
      // Update meta
      state.cartMeta.totalItems = state.cartItems.reduce((sum, item) => sum + item.quantity, 0);
      state.cartMeta.totalAmount = state.cartItems.reduce((sum, item) => sum + item.total_price, 0);
    },
    
    // Remove item completely regardless of quantity
    removeItemCompletely: (state, action) => {
      state.cartItems = state.cartItems.filter(
        (item) => item.id !== action.payload
      );
      
      // Update meta
      state.cartMeta.totalItems = state.cartItems.reduce((sum, item) => sum + item.quantity, 0);
      state.cartMeta.totalAmount = state.cartItems.reduce((sum, item) => sum + item.total_price, 0);
    },
    
    // Clear entire cart
    clearCart: (state) => {
      state.cartItems = [];
      state.resDetails = {
        id: null,
        resName: "",
        resArea: "",
        resImg: "",
        resSla: {},
      };
      state.error = null;
      state.cartMeta = {
        totalItems: 0,
        totalAmount: 0,
      };
    },
    
    // Set restaurant details
    setResDetails: (state, action) => {
      state.resDetails = { 
        ...state.resDetails, 
        ...action.payload 
      };
    },
    
    // Clear restaurant details
    clearResDetails: (state) => {
      state.resDetails = {
        id: null,
        resName: "",
        resArea: "",
        resImg: "",
        resSla: {},
      };
    },
    
    // Set all cart items (from API) - completely replaces cart
    setCartItems: (state, action) => {
      state.cartItems = action.payload.map(item => ({
        ...item,
        quantity: item.quantity || 0,
        total_price: item.total_price || (item.unit_price * item.quantity) || 0
      }));
      
      // Update meta
      state.cartMeta.totalItems = state.cartItems.reduce((sum, item) => sum + item.quantity, 0);
      state.cartMeta.totalAmount = state.cartItems.reduce((sum, item) => sum + item.total_price, 0);
    },
    
    // Update single item quantity
    updateItemQuantity: (state, action) => {
      const { id, quantity } = action.payload;
      
      if (quantity <= 0) {
        // Remove item if quantity is 0 or negative
        state.cartItems = state.cartItems.filter(item => item.id !== id);
      } else {
        const existingItem = state.cartItems.find(item => item.id === id);
        
        if (existingItem) {
          // Update quantity and total price
          existingItem.quantity = quantity;
          if (existingItem.unit_price) {
            existingItem.total_price = existingItem.unit_price * quantity;
          }
        }
      }
      
      // Update meta
      state.cartMeta.totalItems = state.cartItems.reduce((sum, item) => sum + item.quantity, 0);
      state.cartMeta.totalAmount = state.cartItems.reduce((sum, item) => sum + item.total_price, 0);
    },
    
    // Update item details (name, price, image, etc.)
    updateItemDetails: (state, action) => {
      const { id, ...updates } = action.payload;
      const existingItem = state.cartItems.find(item => item.id === id);
      
      if (existingItem) {
        Object.assign(existingItem, updates);
        // Recalculate total price if unit_price changed
        if (updates.unit_price && existingItem.quantity) {
          existingItem.total_price = updates.unit_price * existingItem.quantity;
        }
        
        // Update meta
        state.cartMeta.totalItems = state.cartItems.reduce((sum, item) => sum + item.quantity, 0);
        state.cartMeta.totalAmount = state.cartItems.reduce((sum, item) => sum + item.total_price, 0);
      }
    },
    
    // Merge cart items (useful when loading from server)
    mergeCartItems: (state, action) => {
      const serverItems = action.payload;
      
      serverItems.forEach(serverItem => {
        const localItem = state.cartItems.find(item => item.id === serverItem.id);
        
        if (localItem) {
          // Update existing item with server data
          Object.assign(localItem, serverItem);
        } else {
          // Add new item from server
          state.cartItems.push(serverItem);
        }
      });
      
      // Update meta
      state.cartMeta.totalItems = state.cartItems.reduce((sum, item) => sum + item.quantity, 0);
      state.cartMeta.totalAmount = state.cartItems.reduce((sum, item) => sum + item.total_price, 0);
    },
    
    // Set cart metadata
    setCartMeta: (state, action) => {
      state.cartMeta = {
        ...state.cartMeta,
        ...action.payload
      };
    },
  },
});

export const { 
  addItems, 
  removeItems, 
  removeItemCompletely,
  clearCart, 
  setResDetails,
  clearResDetails,
  setCartItems, 
  updateItemQuantity,
  updateItemDetails,
  mergeCartItems,
  setCartMeta,
  setCartLoading,
  setCartError,
  clearCartError
} = cartSlice.actions;

export default cartSlice.reducer;